import { Period } from '@idc-forms/types/period'
import { z } from 'zod'
import { email, optionalString, requiredString } from '../../primitives/string'
import { MESSAGES } from '../../utils/messages'

/**
 * Estado del flujo de clientes/negociaciones — fuente única de verdad para la
 * validación del API (nestjs-zod `createZodDto`) y del formulario web.
 *
 * `name` es único GLOBAL. `type` es de SISTEMA: las filas init/end/reject/
 * rescind/inactive/undefined son sembradas y bloqueadas — por el API solo se
 * crean estados `process`, y el PATCH no puede cambiar el tipo (el contrato lo
 * omite). `blocked` también es de sistema (fuera del contrato de escritura).
 * Regla de notificaciones: incluir `custom` exige al menos un correo en
 * `emailsNotification`. Los `attachmentRequirements` son el catálogo de
 * requisitos documentales del portal (subdocumentos por estado).
 */

export const STATE_TYPE = {
  INIT: 'init',
  PROCESS: 'process',
  REJECT: 'reject',
  RESCIND: 'rescind',
  INACTIVE: 'inactive',
  END: 'end',
  UNDEFINED: 'undefined'
} as const
export type StateType = (typeof STATE_TYPE)[keyof typeof STATE_TYPE]

/** Destinatarios de la notificación al entrar el expediente al estado. */
export const NOTIFICATION_RECIPIENT = {
  AUTHOR: 'author',
  ADVISOR: 'advisor',
  PRINCIPAL_OWNER: 'principalOwner',
  ALL_OWNERS: 'allOwners',
  CUSTOM: 'custom'
} as const
export type NotificationRecipient =
  (typeof NOTIFICATION_RECIPIENT)[keyof typeof NOTIFICATION_RECIPIENT]

/** Tipo de persona al que aplica un requisito documental (`both` = ambas). */
export const REQUIREMENT_APPLIES_TO = {
  INDIVIDUAL: 'individual',
  JURIDICAL: 'juridical',
  BOTH: 'both'
} as const
export type RequirementAppliesToValue =
  (typeof REQUIREMENT_APPLIES_TO)[keyof typeof REQUIREMENT_APPLIES_TO]

/** `public` lo ve/sube el cliente; `internal` sólo el asesor. */
export const ATTACHMENT_VISIBILITY = {
  PUBLIC: 'public',
  INTERNAL: 'internal'
} as const
export type AttachmentVisibilityValue =
  (typeof ATTACHMENT_VISIBILITY)[keyof typeof ATTACHMENT_VISIBILITY]

// Espejo de PortalValidationType (api client-portal/domain/portal-form-section.ts)
// — valores estables del wire; el dominio del portal sigue siendo el dueño.
export const PORTAL_VALIDATION_TYPE = {
  NEW_CLIENT: 'new_client',
  EXPEDIENT: 'expedient',
  REVALIDATION: 'revalidation',
  PROFILE: 'profile',
  EQUITY: 'equity'
} as const
export type PortalValidationTypeValue =
  (typeof PORTAL_VALIDATION_TYPE)[keyof typeof PORTAL_VALIDATION_TYPE]

/** Requisito documental del estado (subdocumento del portal). */
export const AttachmentRequirementInputSchema = z.object({
  /** Presente al editar un requisito existente; el alta lo genera el API. */
  id: z.string().uuid().optional(),
  name: requiredString(),
  description: optionalString(),
  fileTypesAllowed: z
    .array(requiredString(), { required_error: MESSAGES.REQUIRED })
    .min(1, MESSAGES.REQUIRED),
  maxFileSize: z
    .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
    .min(1, 'Debe ser al menos 1 MB'),
  isRequired: z.boolean().optional(),
  clientRequired: z.boolean().optional(),
  appliesTo: z.nativeEnum(REQUIREMENT_APPLIES_TO).optional(),
  validationTypes: z.array(z.nativeEnum(PORTAL_VALIDATION_TYPE)).optional(),
  visibility: z.nativeEnum(ATTACHMENT_VISIBILITY).optional()
})

const requireEmailsWhenCustom = (
  data: { notification?: NotificationRecipient[]; emailsNotification?: string[] },
  ctx: z.RefinementCtx
) => {
  const hasCustom = data.notification?.includes(NOTIFICATION_RECIPIENT.CUSTOM) ?? false
  if (hasCustom && (data.emailsNotification?.length ?? 0) === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['emailsNotification'],
      message: 'Agrega al menos un correo para la notificación personalizada'
    })
  }
}

const StateInputBaseSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  color: optionalString(),
  /** SLA del estado: cantidad de `interval`s antes de considerarse vencido. */
  time: z
    .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
    .min(1, 'Debe ser al menos 1'),
  interval: z.nativeEnum(Period, {
    required_error: MESSAGES.REQUIRED,
    invalid_type_error: MESSAGES.REQUIRED
  }),
  notification: z.array(z.nativeEnum(NOTIFICATION_RECIPIENT)).optional(),
  emailsNotification: z.array(email()).optional(),
  active: z.boolean().optional(),
  visible: z.boolean().optional(),
  sendSAP: z.boolean().optional(),
  attachmentRequirements: z.array(AttachmentRequirementInputSchema).optional()
})

// El alta solo admite estados de proceso (los tipos de sistema son sembrados);
// mandar otro tipo es un 400 de validación, igual que en el contrato legacy.
export const CreateStateInputSchema = StateInputBaseSchema.extend({
  type: z
    .literal(STATE_TYPE.PROCESS, {
      errorMap: () => ({ message: 'Solo se pueden crear estados de tipo proceso' })
    })
    .optional()
}).superRefine(requireEmailsWhenCustom)

// El PATCH no puede cambiar `type` ni `blocked` (de sistema): el schema los
// omite y el pipe los descarta.
export const UpdateStateInputSchema =
  StateInputBaseSchema.partial().superRefine(requireEmailsWhenCustom)

export type AttachmentRequirementInput = z.infer<typeof AttachmentRequirementInputSchema>
export type CreateStateInput = z.infer<typeof CreateStateInputSchema>
export type UpdateStateInput = z.infer<typeof UpdateStateInputSchema>
